<script setup lang="js">
  import moment from "moment-jalaali";
  import {useI18n} from "vue-i18n";
  import Vue3Datatable from "@bhplugin/vue3-datatable";
  import Lucide from "@/base-components/Lucide";
  import {computed, ref} from "vue";
  import {formatSize} from "@/composables/utility";
  import DatePicker from 'vue3-persian-datetime-picker'
  import BaseInputSelect from "@/base-components/Form/BaseInputSelect.vue";
  import Button from "@/base-components/Button";
  import {useFetch} from "@/composables/useFetch.js";
  import {uri} from "@/constants/config.js";

  moment.loadPersian({usePersianDigits: true});
  const {instance,role} = defineProps({
    instance: {
      type: Object
    },
    role: {
      type: String,
      default: "user",
    },
  })

  const {t: $t} = useI18n()


  const cols = [
    {field: "counter", title: "#"},
    {field: "date", title: $t('title.date')},
    {field: "transfer", title: $t('title.traffic-transfer')},
    {field: "receive", title: $t('title.traffic-receive')},
  ]

  const reportList = [{id: 'hourly', name: 'ساعتی'}, {id: 'daily', name: 'روزانه'}, {id: 'monthly', name: 'ماهانه'}]
  const rows = ref([])
  const isLoading = ref(false)
  const total = computed(() => {
    const total = {date: 'جمع کل', counter: '-', receive: 0, transfer: 0}
    for (const row of rows.value) {
      total.receive += row.receive
      total.transfer += row.transfer
    }
    return total
  })
  const form = ref({
    startDate: new Date(),
    reportType: reportList[0]
  })


  async function fetchData() {
    if (!instance) return;
    isLoading.value = true;
    const urls = {
      user:uri.api.iaas.interface.sub(instance.Interfaces[0].id, 'usage'),
      manager:uri.admin.iaas.interface.sub(instance.Interfaces[0].id, 'usage'),
      cloud:uri.admin.cloud.iaas.interface.sub(instance.Interfaces[0].id, 'usage'),
    }
    const url = urls[role]

    const params = {
      start: form.value.startDate.toJSON(),
      reportType: form.value.reportType.id
    }

    const {data} = await useFetch(url, {params})
    rows.value = data || []
    isLoading.value = false;
  }
</script>

<template>
  <div>
    <div class="grid grid-cols-3 gap-4 mb-2 mt-5 mr-5">
      <div>
        <div class="label-text ms-0.5 mb-2">{{ $t("label.start-date") }}</div>
        <DatePicker v-model="form.startDate" clearable></DatePicker>
      </div>
      <div>
        <BaseInputSelect
          :has-label="true"
          :title="$t('label.report-type')"
          class="primary"
          v-model="form.reportType"
          name="priority"
          deselect-label=""
          :is-multiple="false"
          track-by="id"
          label="name"
          :options="reportList"
          :searchable="false"
          :allow-empty="false"
          :is-preselect-first="true"
        >
        </BaseInputSelect>
      </div>
      <div class="place-content-end">
        <Button variant="primary" size="sm" class="pl-5" @click="fetchData">
          <Lucide icon="Search" class="inline-block w-10" />
          <span class="mr-1">{{ $t("button.search") }}</span>
        </Button>
      </div>
    </div>

    <vue3-datatable
      :columns="cols"
      :rows="rows"
      :loading="isLoading"
      :totalRows="rows.length"
      :isServerMode="true"
      :pageSize="10"
      :pagination="false"
      :sortable="false"
      class="advanced-table px-4 text-start items-start justify-start"
      rowClass="cursor-pointer"
    >
      <!-- Empty header slot to remove the header -->
      <template #header>
        <!-- No content here will hide the header -->
      </template>
      <!-- Empty footer slot to remove the footer -->
      <template #footer>
        <!-- No content here will hide the footer -->
      </template>

      <template #counter="data, key">
        <div class="text-start">
          {{ rows.indexOf(data.value) + 1 }}
        </div>
      </template>
      <template #transfer="data, key">
        <div class="text-start">
          <span dir="ltr">
            {{ formatSize(data.value.transfer, "bytes", 2) }}
          </span>
        </div>
      </template>
      <template #receive="data, key">
        <div class="text-start">
          <span dir="ltr">
            {{ formatSize(data.value.receive, "bytes", 2) }}
          </span>
        </div>
      </template>

      <template #date="data, key">
        <div class="text-start">
          {{
            moment(data.value.date).locale("fa").format("jYYYY/jMM/jDD HH:mm")
          }}
        </div>
      </template>
    </vue3-datatable>
    <div
      class="bh-datatable bh-antialiased bh-relative bh-text-black bh-text-sm bh-font-normal advanced-table px-4 text-start items-start justify-start"
    >
      <div class="bh-table-responsive">
        <table class="bh-table-striped bh-table-hover">
          <thead>
            <tr>
              <th class="bh-select-none bh-z-[1]" colspan="2">جمع کل</th>
              <th class="bh-select-none bh-z-[1]">
                <div class="text-center">
                  <span dir="ltr">
                    {{ formatSize(total.transfer) }}
                  </span>
                </div>
              </th>
              <th class="bh-select-none bh-z-[1] text-center">
                <div class="text-center">
                  <span dir="ltr">
                    {{ formatSize(total.receive) }}
                  </span>
                </div>
              </th>
            </tr>
          </thead>
        </table>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
